import type { ReactNode } from 'react';
import { Dialog as RxDialog, DropdownMenu as RxMenu, Tooltip as RxTooltip, Popover as RxPopover } from 'radix-ui';
import clsx from 'clsx';
import { Button } from './Button';
import './Overlay.css';

/* ------------------------------------------------------------------ *
 * Behaviour from Radix, appearance entirely ours.
 *
 * This is the deliberate split that keeps Prody from looking like a
 * component library: focus traps, dismissal, typeahead, collision
 * handling and ARIA wiring are solved problems and hand-rolling them
 * ships subtly broken keyboard support. Everything visible — the
 * scrim, the corner radius, the motion curve, the way a panel enters —
 * is written here and owes nothing to anyone's design language.
 * ------------------------------------------------------------------ */

/* ---------------- Dialog ----------------
   Enters with a 6px rise and no fade on the panel itself. Only the
   scrim fades. Things in this interface arrive; they do not
   materialise. */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <RxDialog.Root open={open} onOpenChange={onOpenChange}>
      <RxDialog.Portal>
        <RxDialog.Overlay className="pd-scrim" />
        <RxDialog.Content className="pd-dialog" data-size={size}>
          <header className="pd-dialog__head">
            <RxDialog.Title className="t-title pd-dialog__title">{title}</RxDialog.Title>
            <RxDialog.Close asChild>
              <Button variant="quiet" size="sm" iconOnly aria-label="Close" icon={<CloseGlyph />} />
            </RxDialog.Close>
          </header>
          {description ? (
            <RxDialog.Description className="pd-dialog__desc">{description}</RxDialog.Description>
          ) : null}
          <div className="pd-dialog__body scroll-y">{children}</div>
          {footer ? <footer className="pd-dialog__foot">{footer}</footer> : null}
        </RxDialog.Content>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}

/* ---------------- Sheet ----------------
   A drawer off the inline-end edge. Used on narrow viewports where the
   persistent manifest panel cannot fit, and for secondary editors. */

export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  footer,
  side = 'end',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'end' | 'start' | 'bottom';
}) {
  return (
    <RxDialog.Root open={open} onOpenChange={onOpenChange}>
      <RxDialog.Portal>
        <RxDialog.Overlay className="pd-scrim" />
        <RxDialog.Content className="pd-sheet" data-side={side}>
          <header className="pd-sheet__head">
            <RxDialog.Title className="t-title">{title}</RxDialog.Title>
            <RxDialog.Close asChild>
              <Button variant="quiet" size="sm" iconOnly aria-label="Close" icon={<CloseGlyph />} />
            </RxDialog.Close>
          </header>
          <div className="pd-sheet__body scroll-y">{children}</div>
          {footer ? <footer className="pd-sheet__foot">{footer}</footer> : null}
        </RxDialog.Content>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}

/* ---------------- Menu ---------------- */

export interface MenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Right-aligned keyboard hint. */
  shortcut?: string;
  onSelect?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  /** Inserts a rule above this item. */
  separatorBefore?: boolean;
}

export function Menu({
  trigger,
  items,
  align = 'end',
  label,
}: {
  trigger: ReactNode;
  items: MenuItem[];
  align?: 'start' | 'end';
  label: string;
}) {
  return (
    <RxMenu.Root>
      <RxMenu.Trigger asChild aria-label={label}>
        {trigger}
      </RxMenu.Trigger>
      <RxMenu.Portal>
        <RxMenu.Content className="pd-menu" align={align} sideOffset={4} collisionPadding={8}>
          {items.map((item) => (
            <div key={item.key}>
              {item.separatorBefore ? <RxMenu.Separator className="pd-menu__sep" /> : null}
              <RxMenu.Item
                className="pd-menu__item"
                data-tone={item.tone === 'danger' ? 'alert' : undefined}
                disabled={item.disabled ?? false}
                onSelect={item.onSelect ?? (() => undefined)}
              >
                {item.icon ? <span className="pd-menu__icon">{item.icon}</span> : null}
                <span className="pd-menu__label">{item.label}</span>
                {item.shortcut ? (
                  <kbd className="num pd-menu__shortcut">{item.shortcut}</kbd>
                ) : null}
              </RxMenu.Item>
            </div>
          ))}
        </RxMenu.Content>
      </RxMenu.Portal>
    </RxMenu.Root>
  );
}

/* ---------------- Tooltip ----------------
   Instant on the second hover, 400ms on the first — the standard that
   stops a toolbar strobing as you sweep across it. */

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RxTooltip.Provider delayDuration={400} skipDelayDuration={300}>
      {children}
    </RxTooltip.Provider>
  );
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  return (
    <RxTooltip.Root>
      <RxTooltip.Trigger asChild>{children}</RxTooltip.Trigger>
      <RxTooltip.Portal>
        <RxTooltip.Content className="pd-tooltip" side={side} sideOffset={6} collisionPadding={8}>
          {content}
        </RxTooltip.Content>
      </RxTooltip.Portal>
    </RxTooltip.Root>
  );
}

/* ---------------- Popover ---------------- */

export function Popover({
  trigger,
  children,
  align = 'start',
  width,
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  width?: number;
}) {
  return (
    <RxPopover.Root>
      <RxPopover.Trigger asChild>{trigger}</RxPopover.Trigger>
      <RxPopover.Portal>
        <RxPopover.Content
          className="pd-popover"
          align={align}
          sideOffset={4}
          collisionPadding={8}
          style={width ? { inlineSize: width } : undefined}
        >
          {children}
        </RxPopover.Content>
      </RxPopover.Portal>
    </RxPopover.Root>
  );
}

/* ---------------- Confirm ----------------
   Destructive actions state what will happen and name the thing, in
   the same verb the button used. "Cancel order 24-0891" — never
   "Are you sure?". */

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  destructive = true,
  busy = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  destructive?: boolean;
  busy?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="quiet" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            busy={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="pd-dialog__prose">{body}</p>
    </Dialog>
  );
}

function CloseGlyph() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

export const overlayClassNames = { scrim: clsx('pd-scrim') };
